function cleanText(value) {
  return String(value || "")
    .replace(/\r/g, "")
    .replace(/[ \t]+/g, " ")
    .trim();
}

function getLines(text) {
  return String(text || "")
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeNumber(value) {
  if (value === null || value === undefined) return null;

  let raw = String(value).trim();

  // Uzmi samo prvi broj iz stringa: "500 mm" -> "500"
  const match = raw.match(/[\d.,]+/);
  if (!match) return null;

  raw = match[0];

  // EU format: 2.282,177 -> 2282.177
  if (raw.includes(",") && raw.includes(".")) {
    raw = raw.replace(/\./g, "").replace(",", ".");
  } else if (raw.includes(",")) {
    raw = raw.replace(",", ".");
  }

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function extractLineValue(lines, label) {
  const lowerLabel = label.toLowerCase();

  const line = lines.find((x) =>
    x.toLowerCase().startsWith(lowerLabel)
  );

  if (!line) return "";

  return line.slice(label.length).trim();
}

function extractDescription(lines) {
  const partIndex = lines.findIndex((line) =>
    line.toLowerCase().startsWith("part number")
  );

  if (partIndex <= 0) return "";

  const beforePart = lines.slice(0, partIndex);

  const descriptionLines = beforePart.filter((line) => {
    const lower = line.toLowerCase();

    if (lower === "product details") return false;
    if (lower.includes("legal terms")) return false;
    if (lower.includes("julius blum gmbh")) return false;
    if (lower.includes("assumes no legal")) return false;
    if (lower.includes("responsibility")) return false;
    if (lower.includes("warranty")) return false;
    if (/^\d+\s*\/\s*\d+$/.test(lower)) return false;
    if (/^\d{4}-\d{2}-\d{2}/.test(lower)) return false;

    return true;
  });

  return descriptionLines.join(" ").replace(/\s+/g, " ").trim();
}

function getTechLines(lines) {
  const startIndex = lines.findIndex((line) =>
    line.toLowerCase().includes("technical attributes")
  );

  if (startIndex < 0) return [];

  return lines
    .slice(startIndex + 1)
    .filter((line) => {
      const lower = line.toLowerCase();
      if (lower === "attribute value") return false;
      if (lower === "attribute") return false;
      if (lower === "value") return false;
      return true;
    });
}

function extractTechValues(lines) {
  const techLines = getTechLines(lines);

  const specs = [
    ["typeOfRunnerSystem", "type of runner system"],
    ["drawerRunnerVersion", "drawer runner version"],
    ["nominalLength", "Nominal Length"],
    ["dynamicLoadBearing", "dynamic load bearing"],
    ["connectionRunnerSystem", "connection runner system with Box or drawer"],
    ["runnerSystem", "runner system"],
    ["material", "Material"],
    ["colourFinish", "Colour / finish"],
    ["additionalFeatures", "Additional features"],
    ["minimumInternalCabinetDepth", "minimum internal cabinet depth"],
    ["lengthOfCabinetProfile", "length of cabinet profile"],
    ["shankHeightOfCabinetProfile", "Shank height of cabinet profile"],
    ["cabinetProfileWidth", "cabinet profile width"],
    ["mountingWidthInclSpacing", "mounting width (incl. spacing)"],
    ["overExtension", "Over extension"],
    ["cabinetProfileFixing", "cabinet profile fixing"],
    ["itemPackage", "Item package"],
    ["packaging", "packaging"],
    ["blumatic", "Blumatic"],
    ["motionTechnology", "Motion technology"],
    ["pullOutType", "Pull-out type"],
    ["drawerLength", "drawer length"],
  ];

  const result = {};

  for (const line of techLines) {
    const lowerLine = line.toLowerCase();

    for (const [key, label] of specs) {
      const lowerLabel = label.toLowerCase();

      if (!lowerLine.startsWith(lowerLabel)) continue;

      const value = line.slice(label.length).trim();
      if (!value) continue;

      if (result[key]) {
        if (Array.isArray(result[key])) {
          result[key].push(value);
        } else {
          result[key] = [result[key], value];
        }
      } else {
        result[key] = value;
      }

      break;
    }
  }

  return result;
}

function extractPanelThickness(description) {
  const match = String(description || "").match(
    /panel thickness\s+(\d+)\s*-\s*(\d+)\s*mm/i
  );

  return {
    panelThicknessMinMm: match ? Number(match[1]) : null,
    panelThicknessMaxMm: match ? Number(match[2]) : null,
  };
}

function arrayOrNull(value) {
  if (!value) return null;
  return Array.isArray(value) ? value : [value];
}

export function parseProductDetailsText(inputText) {
  const text = cleanText(inputText);
  const lines = getLines(text);

  const description = extractDescription(lines);

  const partNumber = extractLineValue(lines, "Part number");
  const itemNumber = extractLineValue(lines, "Item number");
  const colourFinish = extractLineValue(lines, "Colour / finish");
  const packagingQuantity = normalizeNumber(
    extractLineValue(lines, "Packaging quantity")
  );
  const weightPer1000PcsKg = normalizeNumber(
    extractLineValue(lines, "Weight per 1.000 Pcs in kg")
  );
  const eanCode = extractLineValue(lines, "EAN code") || null;
  const upcCode = extractLineValue(lines, "UPC code") || null;

  const raw = extractTechValues(lines);
  const panelThickness = extractPanelThickness(description);

  const technical = {
    typeOfRunnerSystem: raw.typeOfRunnerSystem || null,
    drawerRunnerVersion: arrayOrNull(raw.drawerRunnerVersion),

    nominalLengthMm: normalizeNumber(raw.nominalLength),
    dynamicLoadBearingKg: normalizeNumber(raw.dynamicLoadBearing),

    connectionRunnerSystem: raw.connectionRunnerSystem || null,
    runnerSystem: raw.runnerSystem || null,
    material: raw.material || null,
    colourFinish: raw.colourFinish || colourFinish || null,
    additionalFeatures: raw.additionalFeatures || null,

    minimumInternalCabinetDepthMm: normalizeNumber(
      raw.minimumInternalCabinetDepth
    ),
    lengthOfCabinetProfileMm: normalizeNumber(raw.lengthOfCabinetProfile),
    shankHeightOfCabinetProfileMm: normalizeNumber(
      raw.shankHeightOfCabinetProfile
    ),
    cabinetProfileWidthMm: normalizeNumber(raw.cabinetProfileWidth),
    mountingWidthInclSpacingMm: normalizeNumber(raw.mountingWidthInclSpacing),
    overExtensionMm: normalizeNumber(raw.overExtension),

    cabinetProfileFixing: raw.cabinetProfileFixing || null,
    itemPackage: raw.itemPackage || null,
    packaging: arrayOrNull(raw.packaging),
    blumatic: raw.blumatic || null,
    motionTechnology: raw.motionTechnology || null,
    pullOutType: raw.pullOutType || null,
    drawerLengthMm: normalizeNumber(raw.drawerLength),

    ...panelThickness,

    handedness: description.toLowerCase().includes("left/right")
      ? "left/right"
      : null,
  };

  const attributes_json = {
    source: {
      vendor: "Blum",
      sourceType: "product_details",
      sourceDocumentType: "text_copy",
      importedAt: new Date().toISOString(),
    },
    commercial: {
      partNumber: partNumber || null,
      itemNumber: itemNumber || null,
      eanCode,
      upcCode,
      packagingQuantity,
      weightPer1000PcsKg,
    },
    technical,
  };

  return {
    code: partNumber || "",
    sku: itemNumber || partNumber || "",
    name: description || partNumber || "",
    brand: "Blum",
    manufacturer: "Julius Blum GmbH",
    finish: colourFinish || technical.colourFinish || "",
    attributes_json,
  };
}